import React, { useState } from "react";
import { toast } from "sonner";

import { useCreateInterface, useSaveCredentials } from "@/shared/api/interfaces";
import { FieldLabel, GhostButton, PrimaryButton, TextInput } from "@/shared/components/settings-atoms";
import { catalogFor } from "@/shared/lib/interface-catalog";
import { FormError } from "./FormError";
import { InterfaceDialog } from "./InterfaceDialog";
import { LinkedAppsWarning } from "./LinkedAppsWarning";
import { Steps } from "./Steps";

import type { SetupProps } from "../types";

const botFather = (
  <a
    href="https://t.me/BotFather"
    target="_blank"
    rel="noopener noreferrer"
    className="font-medium text-[var(--text-primary)] underline underline-offset-2"
  >
    @BotFather
  </a>
);

const NEW_BOT = [
  <>Open {botFather} in Telegram and send <code className="font-mono">/newbot</code>.</>,
  <>Pick a name, then a username that ends in “bot”.</>,
  <>Copy the token it sends back and paste it below.</>,
];

const NEW_TOKEN = [
  <>Open {botFather} and send <code className="font-mono">/mybots</code>.</>,
  <>Pick this bot, then API Token — Revoke gives it a fresh one.</>,
  <>Paste the token below.</>,
];

/** Telegram's answers, in this dialog's words. */
const WORDS = {
  3110001: "Telegram rejected that token — copy it again from @BotFather.",
  3110002: "That bot is already connected to an agent.",
  3110003: "Couldn't reach Telegram — try again.",
};

/**
 * Connect a Telegram bot by its token, or give a broken one a new token.
 *
 * The token is read from the field when it is sent and never held in React
 * state; the write blanks it once the request is done. After an error it stays
 * in the field, so fixing a typo or retrying is one click.
 */
export function TelegramSetup({ agent, existing, onClose }: SetupProps) {
  const create = useCreateInterface();
  const save = useSaveCredentials();
  const write = existing ? save : create;
  const [filled, setFilled] = useState(false);
  const meta = catalogFor("telegram")!;

  const connect = async (values: Record<string, string>) => {
    const credentials = { bot_token: (values.bot_token ?? "").trim() };
    try {
      const connected = existing
        ? await save.send({ id: existing.id, credentials })
        : await create.send({ agent_id: agent.id, type: "telegram", credentials });
      toast.success(`${connected.label ?? meta.name} is live`, {
        description: `People can message ${agent.name} there now.`,
      });
      onClose();
    } catch {
      // FormError shows it under the field, from the mutation's own state.
    }
  };

  return (
    <InterfaceDialog
      meta={meta}
      title={existing ? `Reconnect ${existing.label ?? meta.name}` : "Connect Telegram"}
      description={existing?.error ?? meta.desc}
      onSubmit={connect}
      onClose={onClose}
      footer={
        <>
          <GhostButton type="button" onClick={onClose}>Cancel</GhostButton>
          <PrimaryButton type="submit" disabled={!filled || write.isPending}>
            {write.isPending ? "Connecting…" : "Connect"}
          </PrimaryButton>
        </>
      }
    >
      <Steps items={existing ? NEW_TOKEN : NEW_BOT} />
      <div>
        <FieldLabel>Bot token</FieldLabel>
        <TextInput
          name="bot_token"
          aria-label="Bot token"
          type="password"
          autoComplete="off"
          spellCheck={false}
          placeholder="123456789:AAH…"
          onChange={(value) => {
            setFilled(!!value.trim());
            if (write.error) write.reset();
          }}
        />
      </div>
      <FormError error={write.error} words={WORDS} />
      {!existing && <LinkedAppsWarning agent={agent} />}
    </InterfaceDialog>
  );
}
